"use client"

import { createContext, useContext, useState, ReactNode } from "react"

interface NotificationBannerContextType {
  isBannerVisible: boolean
  setBannerVisible: (visible: boolean) => void
  /** Measured border-box height of the push-notification banner, in px. */
  bannerHeight: number
  setBannerHeight: (height: number) => void
}

// Fallback until the banner reports its measured height (desktop is ~61px,
// mobile ~109px; the text wraps differently per locale).
const DEFAULT_BANNER_HEIGHT = 56

const NotificationBannerContext = createContext<NotificationBannerContextType>({
  isBannerVisible: false,
  setBannerVisible: () => {},
  bannerHeight: 0,
  setBannerHeight: () => {},
})

/**
 * The fixed "Get notified" banner sits at the very top of the viewport. While
 * it is visible, the fixed header is pushed down by the banner's MEASURED
 * height (components/header.tsx) and `--header-h` includes that offset
 * (HeaderHeightContext), so page content moves down with the header instead
 * of sliding under it. A hardcoded 56px offset used to leave the header
 * overlapping the banner and covering the home currency toggle (task-165).
 */
export function NotificationBannerProvider({ children }: { children: ReactNode }) {
  const [isBannerVisible, setIsBannerVisible] = useState(false)
  const [bannerHeight, setBannerHeight] = useState(DEFAULT_BANNER_HEIGHT)

  return (
    <NotificationBannerContext.Provider
      value={{
        isBannerVisible,
        setBannerVisible: setIsBannerVisible,
        bannerHeight,
        setBannerHeight,
      }}
    >
      {children}
    </NotificationBannerContext.Provider>
  )
}

export function useNotificationBanner() {
  return useContext(NotificationBannerContext)
}

/** Vertical offset the fixed header (and everything below it) must clear. */
export function useBannerOffset(): number {
  const { isBannerVisible, bannerHeight } = useNotificationBanner()
  return isBannerVisible ? bannerHeight : 0
}
